import { getUserPermissions } from './permissionsHelper';
import { hasOnboardingChannelReturn } from './onboardingChannelReturn';

// Routes where a forced onboarding redirect would loop back on itself (the
// wizard's own routes) or fight a guard whose decision milestones can't
// override (an inconclusive subscription check, a suspended account) — skip
// the check entirely on these. pricing_table is deliberately NOT here: a
// user with incomplete onboarding milestones should be routed back into the
// wizard even if they land on (or refresh) pricing_table — see
// getLoginRedirectURL in v3/helpers/AuthHelper.js for the equivalent rule
// applied at login time.
const ONBOARDING_EXEMPT_ROUTE_NAMES = [
  'onboarding_shell',
  'onboarding_welcome',
  'onboarding_knowledge_options',
  'onboarding_sandbox',
  'onboarding_channel',
  'subscription_error',
  'account_suspended',
];

// Visited mid-flow when a user starts connecting an inbox from onboarding's
// Channel step (see ChannelInfoModal.vue) and briefly leaves the
// onboarding_shell route tree — some channels round-trip through an
// external OAuth provider before landing back here. Only exempted when the
// accompanying onboardingChannelReturn flag (set right before leaving
// onboarding) is present for that account, so an admin can't route around
// mandatory onboarding by just visiting one of these URLs directly.
export const ONBOARDING_CHANNEL_DETOUR_ROUTE_NAMES = [
  'settings_inboxes_page_channel',
  'settings_inboxes_choose_platform',
  'settings_inboxes_add_agents',
  'settings_inbox_finish',
  // The Instagram OAuth callback lands here when reconnecting an existing inbox.
  'settings_inbox_show',
];

/**
 * Resolves whether the account tied to `accountId` still needs the
 * onboarding wizard, caching the result in the `onboarding` Vuex module so
 * this only re-fetches when the account changes — the cache is kept fresh
 * across the session by markMilestone (called from the wizard steps)
 * committing to the same state.
 *
 * @param {Object} store - Vuex store instance
 * @param {String|Number} accountId - Account ID from route params
 * @returns {Promise<Boolean>} - true if the account still needs onboarding
 */
export async function checkOnboardingStatus(store, accountId) {
  const numericAccountId = Number(accountId);
  const fetchedAccountId =
    store.getters['onboarding/getMilestonesFetchedAccountId'];

  if (fetchedAccountId !== numericAccountId) {
    await store.dispatch(
      'onboarding/fetchOnboardingMilestones',
      numericAccountId
    );
  }

  const milestones = store.getters['onboarding/getMilestones'];
  const milestonesAccountId =
    store.getters['onboarding/getMilestonesFetchedAccountId'];
  // No milestones data, or what's cached belongs to a different account
  // (e.g. this fetch failed and left an earlier account's result in place)
  // — don't block navigation, and don't misread another account's
  // milestones as this one's, on an inconclusive/stale check.
  if (!milestones || milestonesAccountId !== numericAccountId) return false;

  return !milestones.channel_connected;
}

/**
 * Global router guard: bounces an administrator into the onboarding wizard
 * if they haven't finished it yet, regardless of which route they land on
 * — including a plain page refresh. The one-time check in
 * v3/helpers/AuthHelper.js only fires at login/confirmation and is never
 * revisited afterwards, so this covers everything after that point.
 *
 * @param {Object} to - Route object being navigated to
 * @param {Object} from - Route object being navigated from
 * @param {Function} next - Navigation callback
 * @param {Object} store - Vuex store instance
 */
export async function onboardingGuard(to, from, next, store) {
  // Same Cypress-only bypass as subscriptionGuard — see its comment for why
  // this can never evaluate true in a production bundle.
  if (process.env.CYPRESS_TEST_BUILD === 'true' && window.Cypress) {
    return next();
  }

  const accountId = to.params.accountId;
  if (!accountId || ONBOARDING_EXEMPT_ROUTE_NAMES.includes(to.name)) {
    return next();
  }

  if (
    ONBOARDING_CHANNEL_DETOUR_ROUTE_NAMES.includes(to.name) &&
    hasOnboardingChannelReturn(accountId)
  ) {
    return next();
  }

  const user = store.getters.getCurrentUser;
  const permissions = getUserPermissions(user, accountId);
  // Only administrators can access onboarding_shell (see its route meta) —
  // redirecting anyone else there would just bounce straight back via the
  // permission guard in validateLoggedInRoutes, looping forever.
  if (!permissions.includes('administrator')) {
    return next();
  }

  const needsOnboarding = await checkOnboardingStatus(store, accountId);
  if (needsOnboarding) {
    return next({ name: 'onboarding_welcome', params: { accountId } });
  }

  return next();
}
